import firebase from "firebase"
import { db } from "../firebase"

/**
 * Reassigns a booking's agent and writes the full audit trail:
 *   - original_agent (captured lazily on first change)
 *   - not_original_owner (recomputed against original_agent)
 *   - agent_changed_by (UID of the actor)
 *   - agent_history (arrayUnion of { from, to, changed_by, changed_at })
 * Also appends an AGENT_CHANGED log document under bookings/{bookingID}/logs
 * so the change surfaces in the existing Logs tab.
 *
 * This mirrors the behaviour implemented inline in saveBookingDetails
 * (src/utils/providers/booking.js) so that reassignments performed outside
 * the booking page produce an identical audit trail.
 *
 * @param {string} bookingID
 * @param {string} newAgentUID
 * @param {string} actorUID       UID of the user performing the change
 * @param {Object} [agentsByUID]  Optional UID -> "First Last" map for log names
 * @returns {Promise<{changed: boolean, previousAgent?: string}>}
 */
export const reassignBookingAgent = async (bookingID, newAgentUID, actorUID, agentsByUID = {}) => {
	if (!bookingID || !newAgentUID || !actorUID) {
		return { changed: false }
	}

	const snapshot = await db.doc(`bookings/${bookingID}`).get()
	if (!snapshot.exists) return { changed: false }

	const currentData = snapshot.data() || {}
	const previousAgent = currentData.agent

	if (!previousAgent || previousAgent === newAgentUID) {
		return { changed: false, previousAgent }
	}

	const originalAgent = currentData.original_agent || previousAgent

	await db.doc(`bookings/${bookingID}`).set(
		{
			agent: newAgentUID,
			original_agent: originalAgent,
			not_original_owner: newAgentUID !== originalAgent,
			agent_changed_by: actorUID,
			agent_history: firebase.firestore.FieldValue.arrayUnion({
				from: previousAgent,
				to: newAgentUID,
				changed_by: actorUID,
				changed_at: firebase.firestore.Timestamp.now(),
			}),
			last_modified_by: actorUID,
		},
		{ merge: true }
	)

	const fromName = agentsByUID?.[previousAgent] || previousAgent
	const toName = agentsByUID?.[newAgentUID] || newAgentUID
	const byName = agentsByUID?.[actorUID] || actorUID

	await db.collection(`bookings/${bookingID}/logs`).add({
		type: "AGENT_CHANGED",
		badge: "INFO",
		message: `Booking agent was changed from ${fromName} to ${toName} by ${byName}`,
		user: actorUID,
		created: firebase.firestore.FieldValue.serverTimestamp(),
		metadata: {
			from: previousAgent,
			to: newAgentUID,
			changed_by: actorUID,
		},
	})

	return { changed: true, previousAgent }
}
